import { useEffect } from "react";

/* Lets any screen publish the data it's currently showing so the global AI
 * Assist can reason and act on "what's on my screen" (e.g. the certificate
 * rows, the device list). A simple module singleton — AIAssist reads it at
 * send time; no provider wiring needed. Keep payloads compact (the AI only
 * needs identifiers + a few fields, not full objects). */

export type AIScreenData = {
  label: string; // human name of the screen, e.g. "Certificates"
  summary?: string; // optional one-liner of filters/scope in effect
  items: Record<string, unknown>[]; // the visible rows, trimmed to key fields
  /** The controls this screen ACTUALLY has, exactly as they are labelled.
   *
   * The assistant is told to name "the exact button or menu" but was given no
   * idea which ones exist, so it invented them: it instructed a technician to
   * click a "Create ticket from alert" item on the Alerts row menu, plus a bulk
   * "Create tickets" action and a "Link alert -> auto-resolve ticket" checkbox.
   * None of the three had ever been built. Confident, specific, wrong — the
   * worst shape a help answer can take.
   *
   * Publish the real labels and the model is told to use only these. Omitting
   * it is safe: the backend then says nothing about controls at all rather
   * than guessing.
   */
  actions?: string[];
};

let _data: AIScreenData | null = null;

export function setAIScreenData(d: AIScreenData | null): void {
  _data = d;
}

export function getAIScreenData(): AIScreenData | null {
  return _data;
}

/** Publish the current screen's visible data for the AI Assist; clears on
 * unmount. Cap the item count so we never ship a huge payload to the model. */
export function usePublishScreenData(
  label: string,
  items: Record<string, unknown>[],
  summary?: string,
  cap = 100,
  actions?: string[],
): void {
  // Joined to a primitive so a fresh array literal on every render does not
  // restart the effect forever.
  const actionsKey = (actions || []).join("|");
  useEffect(() => {
    setAIScreenData({
      label,
      summary,
      items: (items || []).slice(0, cap),
      actions: actionsKey ? actionsKey.split("|") : undefined,
    });
    return () => setAIScreenData(null);
  }, [label, summary, cap, items, actionsKey]);
}
